import {
  Box,
  Chip,
  Link,
  List,
  ListItem,
  ListItemText,
  Typography,
  useTheme,
} from '@mui/material';
import { DrawerStickyHeader } from 'components/DrawerStickyHeader';
import { MapEntityDrawerContext } from 'features/mapping/components/MapEntityDrawer';
import { MapEntityDrawerHeader } from 'features/mapping/components/MapEntityDrawerHeader';
import { useContext } from 'react';
import { getResponsiveFontSize } from 'theme/watchdutyTheme.utils';
import { InlineStatus } from 'components/InlineStatus';
import { useTranslation } from 'react-i18next';
import { ExternalLayerFeatureEntity } from 'features/mapping/mapping.types';
import { useExternalFeatureQuery } from './useExternalFeatureQuery';
import {
  isImageExtensionUrl,
  isHttpsUrl,
  tryFormatTimestamp,
} from './ExternalFeatureDrawerContent.utils';
import { EMPTY_DATA_VALUE } from '../../../../../constants';

const renderAttributeValue = (
  key: string,
  value: unknown,
): string | JSX.Element => {
  if (value === null || value === undefined) return EMPTY_DATA_VALUE;

  const timestamp = tryFormatTimestamp(key, value);
  if (timestamp) return timestamp;

  const strValue = String(value);

  if (!isHttpsUrl(strValue)) {
    return strValue || EMPTY_DATA_VALUE;
  }

  if (isImageExtensionUrl(strValue)) {
    return (
      <Link href={strValue} target="_blank" rel="noopener noreferrer">
        <Box
          component="img"
          src={strValue}
          alt={key.replace(/_/g, ' ')}
          sx={{
            maxWidth: '100%',
            height: 'auto',
            borderRadius: 1.5,
          }}
        />
      </Link>
    );
  }

  return (
    <Link
      href={strValue}
      target="_blank"
      rel="noopener noreferrer"
      sx={{ wordBreak: 'break-word' }}
    >
      {strValue}
    </Link>
  );
};

export const ExternalFeatureDrawerContent = (
  props: ExternalLayerFeatureEntity,
): JSX.Element => {
  const { sourceId, featureId, displayName } = props;
  const { scrollElementRef } = useContext(MapEntityDrawerContext);
  const theme = useTheme();
  const { t } = useTranslation();
  const { externalFeature, isLoading, isError } = useExternalFeatureQuery({
    sourceId,
    featureId,
  });

  return (
    <Box>
      <DrawerStickyHeader
        scrollContainer={scrollElementRef?.current as HTMLElement}
      >
        <DrawerStickyHeader.ContentContainer>
          <MapEntityDrawerHeader>
            <Typography
              component="div"
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Typography component="span" noWrap>
                {displayName}
              </Typography>
              <Chip
                label={t('map.layers.private')}
                variant="watchduty"
                color="error"
                sx={{ marginLeft: 1 }}
                component="span"
              />
            </Typography>
          </MapEntityDrawerHeader>
        </DrawerStickyHeader.ContentContainer>
      </DrawerStickyHeader>

      {(isLoading || isError || !externalFeature) && (
        <InlineStatus status={isLoading ? 'loading' : 'error'} />
      )}

      {Boolean(externalFeature) && (
        <List>
          {Object.entries(externalFeature!.attributes).map(([key, value]) => (
            <ListItem key={key} divider>
              <ListItemText
                primary={key.replace(/_/g, ' ')}
                secondary={renderAttributeValue(key, value)}
                slotProps={{
                  primary: {
                    color: 'secondary',
                    variant: 'h4',
                    sx: {
                      fontWeight: 'fontWeightBold',
                      fontSize: getResponsiveFontSize(
                        theme.typography.pxToRem(18),
                      ),
                    },
                  },
                  secondary: {
                    component: 'div',
                    color: 'textPrimary',
                    fontWeight: 'fontWeightMedium',
                  },
                }}
              />
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
};
